import { useMemo } from 'react'
import { sortBy } from 'lib/array/arrayUtils'
import useQueryParams from 'hooks/useQueryParams'
import type { OfferPageMultiRoomAutoselectionReason, OfferPageMultiRoomMode, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { GOOGLE_ADS_UTM_CONTENT } from 'constants/bedbank'
import Sheet from 'components/utils/Sheet'
import OfferPageRoomModeToggleSection from 'components/OfferPage/MultipleRoomsBooking/Mobile/OfferPageRoomModeToggleSection'
import OfferPageTogglesSection from 'components/OfferPage/MultipleRoomsBooking/Mobile/OfferPageTogglesSection'
import BedbankMultiSameRoomRoom from '../MultiRoom/BedbankMultiSameRoomRoom'
import BedbankMultiIndividualRoomRoom from '../MultiRoom/BedbankMultiIndividualRoomRoom'
import { getCanUseMultiRoomUI } from 'selectors/offerPage/bedbankOfferPageSelectors'
import uuidV4 from 'lib/string/uuidV4Utils'
import useBedbankFlightPrice from 'hooks/OfferPage/useBedbankFlightPrice'
import BedbankMultiIndividualRoomsFooter from '../MultiRoom/BedbankMultiIndividualRoomsFooter'
import { useRoomTypeAvailabilityStatuses } from 'hooks/useBedbankPackageViews'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'
import OfferPageLoyaltyPointsSlider from 'components/OfferPage/Loyalty/OfferPageLoyaltyPointsSlider'

interface Props {
  checkInDate?: string
  checkOutDate?: string
  offer: App.BedbankOffer
  onViewPackageOptions?: () => void
  packageViews: Array<App.BedbankOfferPackageView>
  fetchingRates?: boolean
  rooms: Array<OfferPageRoom>
  canUseMultiRoomUI: boolean
  roomMode: OfferPageMultiRoomMode
  autoselectionReason?: OfferPageMultiRoomAutoselectionReason
  /** This one will be shown at the top of the list */
  topPackageId?: string
  flightOrigin?: App.Airport
  multipleRoomBookingActive: boolean
}

function BedbankRoomOptionsMobile(props: Props) {
  const {
    checkInDate,
    checkOutDate,
    offer,
    packageViews,
    fetchingRates,
    rooms,
    canUseMultiRoomUI,
    roomMode,
    autoselectionReason,
    topPackageId: topPackageIdProp,
    flightOrigin,
    multipleRoomBookingActive,
  } = props

  const roomTypeAvailabilityStatus = useRoomTypeAvailabilityStatuses(offer, rooms, packageViews)

  const query = useQueryParams()
  const validCheckinCheckoutDates = !!(checkInDate && checkOutDate)
  const utmContent = query.get('utm_content')
  const highlightedRoomTypeId = utmContent === GOOGLE_ADS_UTM_CONTENT ? query.get('roomTypeId') : null
  const topPackageId = highlightedRoomTypeId || topPackageIdProp

  const hasPromotions = offer.promotions.length > 0

  const sortedPackages = useMemo(() => {
    const sortedPackageViews = sortBy(packageViews, pkg => {
      if (pkg.cheapestRate) {
        // Added especially for promotional rates https://aussiecommerce.atlassian.net/browse/HC-768
        const PROMO_SORT_PRIORITY_DIVISOR = 1000
        if (pkg.cheapestRate.promotionId) {
          return (pkg.cheapestRate.totals.inclusive + (pkg.cheapestRate.totals.propertyFees ?? 0)) / PROMO_SORT_PRIORITY_DIVISOR
        }
        return pkg.cheapestRate.totals.inclusive + (pkg.cheapestRate.totals.propertyFees ?? 0)
      } else if (hasPromotions) {
        return Number.MAX_SAFE_INTEGER
      }

      if (validCheckinCheckoutDates) {
        if (!pkg.capacityValid) {
          return Number.MAX_SAFE_INTEGER - 1
        }

        return Number.MAX_SAFE_INTEGER
      }

      if (pkg.capacityValid) {
        return Number.MIN_SAFE_INTEGER
      }

      return Number.MAX_SAFE_INTEGER
    }, 'asc')

    const highlightedPackage = sortedPackageViews.find(p => p.package.roomId === topPackageId)
    if (highlightedPackage) {
      return [
        highlightedPackage,
        ...sortedPackageViews.filter(p => p.package.roomId !== topPackageId),
      ]
    }
    else {
      return sortedPackageViews
    }
  }, [packageViews, hasPromotions, validCheckinCheckoutDates, topPackageId])

  const displayablePackages = useMemo(() => {
    const showAll = sortedPackages.filter(p => p.available).length === 0
    return sortedPackages.filter(packageView => packageView.show && (roomTypeAvailabilityStatus[packageView.package.roomId] !== 'unavailable' || showAll))
  }, [sortedPackages, roomTypeAvailabilityStatus])

  const roomListId = useMemo(() => uuidV4(),
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [displayablePackages]) // regenerate id if packages change

  const totalFlightPrice = useBedbankFlightPrice(offer, {
    rooms,
    startDate: checkInDate,
    endDate: checkOutDate,
    originAirportCode: flightOrigin?.code,
  })

  return <Sheet background="light-background" padding="8 0">
    <VerticalSpacer gap={8}>
      {canUseMultiRoomUI && <OfferPageRoomModeToggleSection
        roomMode={roomMode}
        rooms={rooms}
        saleUnit={offer.saleUnit}
        autoselectionReason={autoselectionReason}
      />}

      <OfferPageTogglesSection totalFlightPrice={totalFlightPrice} />

      <OfferPageLoyaltyPointsSlider />

      {multipleRoomBookingActive && <>
        {rooms.map((room, i) => {
          return <BedbankMultiIndividualRoomRoom
            key={room.id}
            room={room}
            packageNumber={i + 1}
          />
        })}
        <BedbankMultiIndividualRoomsFooter />
      </>}
      {!multipleRoomBookingActive && displayablePackages.map((packageView, i) => <BedbankMultiSameRoomRoom
        key={packageView.package.roomId}
        packageView={packageView}
        fetchingRates={fetchingRates}
        roomListId={roomListId}
        roomPosition={i + 1}
        youWereInterested={utmContent === GOOGLE_ADS_UTM_CONTENT && highlightedRoomTypeId === packageView.package.roomId}
        availabilityStatus={roomTypeAvailabilityStatus[packageView.package.roomId]}
        ratesLayout="carousel"
      />)}
    </VerticalSpacer>
  </Sheet>
}

type MappedProps = Pick<Props,
  | 'checkInDate'
  | 'checkOutDate'
  | 'rooms'
  | 'canUseMultiRoomUI'
  | 'roomMode'
  | 'autoselectionReason'
  | 'flightOrigin'
  | 'multipleRoomBookingActive'
> & { open: boolean }

export default withOfferPageAndReduxState((state, appState): MappedProps => {
  return {
    checkInDate: state.checkInDate,
    checkOutDate: state.checkOutDate,
    open: !!state.activeFilter,
    rooms: state.rooms,
    canUseMultiRoomUI: getCanUseMultiRoomUI(state, appState),
    roomMode: state.multiRoomMode,
    autoselectionReason: state.multiRoomAutoselectionReason,
    flightOrigin: state.airport,
    multipleRoomBookingActive: isMultipleRoomBookingActive(state, appState),
  }
}, BedbankRoomOptionsMobile)
